/**
 * BusinessTermConfigModal.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Modal for configuring business term handling in a DG discovery run.
 *
 * Entry points:
 *   1. DataInputPage — "📚 Business Terms" button (tool=BUILD_DG_USING_DATA)
 *   2. DGConfigModal — "Business Element Name" attribute row → "⚙ Configure →"
 *
 * Three modes:
 *   A. "generate"      — LLM creates terms from scratch (default)
 *   B. "map_glossary"  — map to terms already in ADRIVA glossary
 *   C. "import_map"    — import from Excel / Collibra / Alation
 *                        Terms saved to ADRIVA glossary AND returned in onSave()
 *                        Mode auto-becomes "excel" | "collibra" | "alation"
 *
 * CRITICAL DATA FLOW:
 *   onSave(cfg) fires with:
 *     cfg.mode    — "generate" | "map_glossary" | "excel" | "collibra" | "alation"
 *     cfg.terms[] — imported term objects (non-empty after import)
 *
 *   Caller (AttributeRow bridge in DGConfigModal) maps:
 *     cfg → BusinessTermModeConfig → DGAppConfig.businessTermConfig
 *
 *   dg_config_merge.py then extracts:
 *     businessTermConfig.terms[] → dg_config["business_terms"] for the agent
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { adriva_catalog_postAction, apiPost, apiGet } from "../API";

// ─── TYPES ────────────────────────────────────────────────────────────────────

export type BTMode = "generate" | "map_glossary" | "excel" | "collibra" | "alation";

export interface ImportedTerm {
  id:           string;
  term:         string;
  definition?:  string;
  description?: string;
  domain?:      string;
  synonyms?:    string[];
  source?:      "excel" | "collibra" | "alation";
  source_id?:   string;
}

export interface BusinessTermRunConfig {
  mode:           BTMode;
  terms:          ImportedTerm[];
  glossary_count: number;
  entity_filter:  string;
}

export interface BusinessTermConfigModalProps {
  show:     boolean;
  onHide:   () => void;
  appNm:    string;
  entity:   string;
  entityId: string;
  config: {
    mode?:                BTMode;
    terms?:               ImportedTerm[];
    glossary_count?:      number;
    entity_filter?:       string;
    glossary_term_count?: number; // legacy field name
  };
  onChange: (cfg: BusinessTermRunConfig) => void;
  onSave:   (cfg: BusinessTermRunConfig) => void;
}

interface GlossaryPreviewTerm { id: string; term: string; domain: string; }

interface CatalogCreds {
  host:       string;
  username?:  string;
  password?:  string;
  api_token?: string;
  auth_type:  "basic" | "token";
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────

const T = {
  bg:          "#f8fafc",
  bgCard:      "#ffffff",
  bgSection:   "#f5f7fa",
  textPrimary: "#0f172a",
  textMuted:   "#64748b",
  textLabel:   "#374151",
  brand:       "#4f46e5",
  brandLight:  "#eef2ff",
  brandMid:    "#c7d2fe",
  border:      "#e2e8f0",
  borderFocus: "#6366f1",
  green:       "#059669",
  greenBg:     "#f0fdf4",
  greenBorder: "#6ee7b7",
  amber:       "#d97706",
  amberBg:     "#fffbeb",
  amberBorder: "#fde68a",
  red:         "#dc2626",
  redBg:       "#fef2f2",
  redBorder:   "#fecaca",
};

// ─── SHARED STYLE HELPERS ─────────────────────────────────────────────────────

const inputSt: React.CSSProperties = {
  width: "100%", background: T.bgCard, border: `1px solid ${T.border}`,
  borderRadius: 6, padding: "8px 11px", color: T.textPrimary,
  fontSize: "0.82rem", outline: "none", fontFamily: "inherit",
};

const labelSt: React.CSSProperties = {
  fontSize: "0.68rem", fontWeight: 700, color: T.textLabel,
  textTransform: "uppercase" as const, letterSpacing: "0.07em",
  display: "block", marginBottom: 5,
};

const sectionLabelSt: React.CSSProperties = {
  fontSize: "0.62rem", fontWeight: 700, color: T.textMuted,
  textTransform: "uppercase" as const, letterSpacing: "0.08em",
  marginBottom: 6,
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function postCatalogAction(p: object): Promise<any> {
  return new Promise(resolve => {
    adriva_catalog_postAction(
      JSON.stringify(p),
      (res: any) => resolve(res),
      (err: any) => resolve({ success: false, msg: err?.message ?? "Network error" }),
    );
  });
}

function parseExcelCSV(content: string, fileName: string): ImportedTerm[] {
  const lines = content.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const sep    = fileName.endsWith(".tsv") ? "\t" : ",";
  const header = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/"/g, ""));
  const col    = (names: string[], row: string[]) => {
    for (const n of names) {
      const i = header.indexOf(n);
      if (i >= 0) return (row[i] || "").replace(/^"|"$/g, "").trim();
    }
    return "";
  };
  return lines.slice(1).map((line, i): ImportedTerm => {
    const row = line.split(sep);
    return {
      id:         `EXCEL-${String(i + 1).padStart(4, "0")}`,
      term:       col(["term", "name", "business term", "businessterm"], row),
      definition: col(["definition", "description", "desc", "meaning"], row),
      domain:     col(["domain", "data domain", "category"], row),
      synonyms:   col(["synonyms", "alias", "aliases"], row).split(";").map(s => s.trim()).filter(Boolean),
      source:     "excel",
    };
  }).filter(t => t.term);
}

// ─── RADIO DOT ────────────────────────────────────────────────────────────────

function RadioDot({ active }: { active: boolean }) {
  return (
    <div style={{
      width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
      border: `2px solid ${active ? T.brand : T.border}`,
      background: active ? T.brand : T.bgCard,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {active && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />}
    </div>
  );
}

// ─── CHECKBOX ────────────────────────────────────────────────────────────────

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <div style={{
      width: 14, height: 14, borderRadius: 3, flexShrink: 0, marginTop: 2,
      border: `1.5px solid ${checked ? T.brand : T.border}`,
      background: checked ? T.brand : T.bgCard,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {checked && (
        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
          <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );
}

// ─── TAG PILL ─────────────────────────────────────────────────────────────────

function TagPill({ label }: { label: string }) {
  return (
    <span style={{
      fontSize: "0.65rem", padding: "2px 8px", borderRadius: 20,
      background: T.brandLight, color: T.brand, border: `1px solid ${T.brandMid}`,
      display: "inline-block",
    }}>
      {label}
    </span>
  );
}

// ─── TERM TABLE ───────────────────────────────────────────────────────────────

function TermTable({
  terms, selected, onToggle, selectable = false, maxH = 260,
}: {
  terms:      Array<{ id: string; term: string; definition?: string; description?: string; domain?: string }>;
  selected?:  Set<string>;
  onToggle?:  (id: string) => void;
  selectable?: boolean;
  maxH?:      number;
}) {
  if (!terms.length) return null;
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", flex: 1, minHeight: 0 }}>
      {/* Header */}
      <div style={{
        display: "grid",
        gridTemplateColumns: selectable ? "24px 1fr 1.5fr 90px" : "1fr 1.5fr 90px",
        padding: "5px 10px", background: T.bgSection, borderBottom: `1px solid ${T.border}`,
        position: "sticky", top: 0, zIndex: 1,
      }}>
        {selectable && <span />}
        {["Term", "Definition", "Domain"].map(h => (
          <span key={h} style={{
            fontSize: "0.6rem", fontWeight: 700, color: T.textMuted,
            textTransform: "uppercase", letterSpacing: "0.07em",
          }}>{h}</span>
        ))}
      </div>
      <div style={{ maxHeight: maxH, overflowY: "auto" }}>
        {terms.map((t, i) => {
          const sel  = selected?.has(t.id) ?? false;
          const defn = t.definition || t.description || "";
          return (
            <div
              key={t.id}
              onClick={() => selectable && onToggle?.(t.id)}
              style={{
                display: "grid",
                gridTemplateColumns: selectable ? "24px 1fr 1.5fr 90px" : "1fr 1.5fr 90px",
                padding: "7px 10px", gap: 8, alignItems: "start",
                background: sel ? T.brandLight : i % 2 === 0 ? T.bgCard : T.bg,
                borderBottom: `1px solid ${T.border}`,
                cursor: selectable ? "pointer" : "default",
                borderLeft: sel ? `3px solid ${T.brand}` : "3px solid transparent",
                transition: "background 0.08s",
              }}
            >
              {selectable && <Checkbox checked={sel} />}
              <div style={{ fontSize: "0.78rem", fontWeight: 600, color: T.textPrimary }}>{t.term}</div>
              <div style={{ fontSize: "0.72rem", color: T.textMuted, lineHeight: 1.4 }}>
                {defn.length > 110 ? defn.slice(0, 110) + "…" : defn}
              </div>
              <div style={{ fontSize: "0.68rem", color: T.textMuted }}>
                {t.domain
                  ? <span style={{
                      fontSize: "0.65rem", padding: "2px 6px", borderRadius: 4,
                      background: T.bgSection, border: `1px solid ${T.border}`,
                    }}>{t.domain}</span>
                  : "—"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── EXCEL IMPORT PANEL ───────────────────────────────────────────────────────

function ExcelImportPanel({
  appNm, onTermsImported,
}: {
  appNm: string;
  onTermsImported: (terms: ImportedTerm[]) => void;
}) {
  const [terms,    setTerms]    = useState<ImportedTerm[]>([]);
  const [parsing,  setParsing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setParsing(true);
    const reader = new FileReader();
    reader.onload = ev => {
      setTerms(parseExcelCSV(ev.target?.result as string, f.name));
      setParsing(false);
    };
    reader.readAsText(f);
  };

  const handleSave = async () => {
    if (!terms.length) return;
    setSaving(true);
    const r = await apiPost(`/adriva_api/dg/glossary/${appNm}/upsert`, {
      terms: terms.map(t => ({
        term: t.term, description: t.definition, domain: t.domain,
        synonyms: t.synonyms, source: "excel", status: "draft",
      })),
    });
    setSaving(false);
    if (r.success) {
      setSavedMsg(`✓ ${r.created} new, ${r.updated} updated`);
      onTermsImported(terms);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
      <input ref={fileRef} type="file" accept=".csv,.tsv" style={{ display: "none" }} onChange={handleFile} />

      {terms.length === 0 ? (
        <>
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              border: `1.5px dashed ${T.border}`, borderRadius: 10, padding: "28px 20px",
              textAlign: "center", cursor: "pointer", background: T.bgSection,
              transition: "border-color 0.1s",
            }}
            onMouseOver={e => (e.currentTarget.style.borderColor = T.brand)}
            onMouseOut={e  => (e.currentTarget.style.borderColor = T.border)}
          >
            {parsing ? (
              <div style={{ color: T.textMuted, fontSize: "0.82rem" }}>Parsing…</div>
            ) : (
              <>
                <div style={{ fontSize: "2rem", marginBottom: 8 }}>📄</div>
                <div style={{ fontSize: "0.84rem", fontWeight: 600, color: T.brand, marginBottom: 5 }}>
                  Click to upload CSV / Excel-exported CSV
                </div>
                <div style={{ fontSize: "0.72rem", color: T.textMuted, lineHeight: 1.5 }}>
                  Required: <code>term</code>, <code>definition</code>
                  &nbsp;·&nbsp; Optional: <code>domain</code>, <code>synonyms</code>
                </div>
              </>
            )}
          </div>
          <div>
            <div style={sectionLabelSt}>Accepted formats</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[".csv", ".tsv", "Excel → Save As CSV"].map(f => (
                <span key={f} style={{
                  fontSize: "0.68rem", padding: "2px 8px", borderRadius: 4,
                  background: T.bgSection, border: `1px solid ${T.border}`, color: T.textMuted,
                }}>{f}</span>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ ...sectionLabelSt, marginBottom: 0 }}>Preview — {terms.length} terms</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {savedMsg && <span style={{ fontSize: "0.72rem", fontWeight: 700, color: T.green }}>{savedMsg}</span>}
              <button
                onClick={() => { setTerms([]); setSavedMsg(""); fileRef.current?.click(); }}
                style={{
                  padding: "3px 10px", borderRadius: 5, cursor: "pointer",
                  background: T.bgCard, border: `1px solid ${T.border}`,
                  color: T.textMuted, fontSize: "0.72rem",
                }}
              >Replace</button>
              {!savedMsg && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: "5px 14px", borderRadius: 6, cursor: "pointer",
                    background: T.brand, border: `1px solid ${T.brand}`,
                    color: "#fff", fontSize: "0.76rem", fontWeight: 700,
                    opacity: saving ? 0.7 : 1,
                  }}
                >{saving ? "Saving…" : "Save to Glossary"}</button>
              )}
            </div>
          </div>
          <TermTable terms={terms.slice(0, 100)} maxH={280} />
          {terms.length > 100 && (
            <div style={{ fontSize: "0.7rem", color: T.textMuted, textAlign: "center" }}>
              Showing first 100 of {terms.length} terms
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── COLLIBRA IMPORT PANEL ────────────────────────────────────────────────────

function CollibraImportPanel({
  appNm, entityId, onTermsImported,
}: {
  appNm: string;
  entityId: string;
  onTermsImported: (terms: ImportedTerm[]) => void;
}) {
  const [creds,       setCreds]       = useState<CatalogCreds>({ host: "", auth_type: "basic" });
  const [testState,   setTestState]   = useState<"idle" | "testing" | "ok" | "error">("idle");
  const [testMsg,     setTestMsg]     = useState("");
  const [communities, setCommunities] = useState<any[]>([]);
  const [domains,     setDomains]     = useState<Record<string, any[]>>({});
  const [expanded,    setExpanded]    = useState<Set<string>>(new Set());
  const [domLoading,  setDomLoading]  = useState<Record<string, boolean>>({});
  const [selDomain,   setSelDomain]   = useState<{ id: string; name: string } | null>(null);
  const [btList,      setBtList]      = useState<any[]>([]);
  const [btTotal,     setBtTotal]     = useState(0);
  const [btOffset,    setBtOffset]    = useState(0);
  const [btSearch,    setBtSearch]    = useState("");
  const [btLoading,   setBtLoading]   = useState(false);
  const [selected,    setSelected]    = useState<Set<string>>(new Set());
  const [saving,      setSaving]      = useState(false);
  const [savedMsg,    setSavedMsg]    = useState("");
  const BT_LIMIT = 50;

  const handleTest = async () => {
    setTestState("testing");
    const r = await postCatalogAction({
      action: "TEST_CONNECTION", target: "collibra",
      entity_id: entityId, app_nm: appNm, creds,
    });
    if (r.success) {
      setTestState("ok");
      const cr = await postCatalogAction({
        action: "FETCH_COMMUNITIES", target: "collibra",
        entity_id: entityId, app_nm: appNm, creds,
      });
      if (cr.success) setCommunities(cr.data?.communities ?? []);
    } else {
      setTestState("error");
      setTestMsg(r.msg ?? "Connection failed");
    }
  };

  const toggleCommunity = async (cid: string) => {
    const next = new Set(expanded);
    if (next.has(cid)) { next.delete(cid); setExpanded(next); return; }
    next.add(cid); setExpanded(next);
    if (domains[cid]) return;
    setDomLoading(p => ({ ...p, [cid]: true }));
    const r = await postCatalogAction({
      action: "FETCH_COMMUNITY_DOMAINS", target: "collibra",
      entity_id: entityId, app_nm: appNm, creds, community_id: cid,
    });
    setDomLoading(p => ({ ...p, [cid]: false }));
    if (r.success) setDomains(p => ({ ...p, [cid]: r.data?.domains ?? [] }));
  };

  const fetchBTs = async (domainId: string, offset: number, search: string, replace: boolean) => {
    setBtLoading(true);
    const r = await postCatalogAction({
      action: "FETCH_BUSINESS_TERMS", target: "collibra",
      entity_id: entityId, app_nm: appNm, creds,
      domain_id: domainId, offset, limit: BT_LIMIT, search,
    });
    setBtLoading(false);
    if (r.success) {
      setBtTotal(r.data.total ?? 0);
      setBtList(replace ? r.data.terms : (p: any[]) => [...p, ...r.data.terms]);
      setBtOffset(offset + r.data.terms.length);
    }
  };

  const handleDomainSelect = (dom: any) => {
    setSelDomain({ id: dom.id, name: dom.name });
    setBtList([]); setBtOffset(0); setBtSearch(""); setSelected(new Set());
    fetchBTs(dom.id, 0, "", true);
  };

  const handleSearch = (s: string) => {
    setBtSearch(s);
    if (selDomain) { setBtList([]); setBtOffset(0); fetchBTs(selDomain.id, 0, s, true); }
  };

  const handleSave = async () => {
    const toSave = btList.filter(t => selected.has(t.id));
    if (!toSave.length) return;
    setSaving(true);
    await apiPost(`/adriva_api/dg/glossary/${appNm}/upsert`, {
      terms: toSave.map(t => ({
        term: t.name || t.term, description: t.definition || t.description,
        domain: t.domain, source: "collibra", collibra_id: t.id, status: "draft",
      })),
    });
    setSaving(false);
    const imported: ImportedTerm[] = toSave.map(t => ({
      id: t.id, term: t.name || t.term, definition: t.definition || t.description,
      domain: t.domain, source: "collibra" as const, source_id: t.id,
    }));
    setSavedMsg(`✓ ${imported.length} terms saved`);
    onTermsImported(imported);
  };

  const toggleSel = (id: string) =>
    setSelected(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // ── Connection form ──────────────────────────────────────────────────────────
  if (testState !== "ok") return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 10,
      padding: "14px 16px", background: T.bgCard,
      border: `1px solid ${T.border}`, borderRadius: 8,
    }}>
      <div>
        <label style={labelSt}>Collibra Host URL</label>
        <input
          style={inputSt}
          value={creds.host}
          onChange={e => setCreds({ ...creds, host: e.target.value })}
          placeholder="https://yourorg.collibra.com"
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={labelSt}>Username</label>
          <input style={inputSt} value={creds.username ?? ""} autoComplete="off"
            onChange={e => setCreds({ ...creds, username: e.target.value })} />
        </div>
        <div>
          <label style={labelSt}>Password</label>
          <input style={inputSt} type="password" value={creds.password ?? ""} autoComplete="off"
            onChange={e => setCreds({ ...creds, password: e.target.value })} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          onClick={handleTest}
          disabled={testState === "testing"}
          style={{
            padding: "6px 18px", borderRadius: 6, cursor: "pointer",
            background: T.brand, border: `1px solid ${T.brand}`,
            color: "#fff", fontSize: "0.78rem", fontWeight: 700,
            opacity: testState === "testing" ? 0.7 : 1,
          }}
        >{testState === "testing" ? "Connecting…" : "Connect"}</button>
        {testState === "error" && (
          <span style={{ fontSize: "0.72rem", color: T.red }}>{testMsg}</span>
        )}
      </div>
    </div>
  );

  // ── Connected: two-pane browser ──────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 0 }}>

      {/* Connected bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "7px 12px",
        background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 6,
      }}>
        <span style={{ fontSize: "0.78rem", fontWeight: 700, color: T.green }}>
          ✓ Connected · {creds.host}
        </span>
        <button
          onClick={() => setTestState("idle")}
          style={{
            marginLeft: "auto", background: "none", border: `1px solid ${T.greenBorder}`,
            color: T.green, borderRadius: 4, padding: "2px 8px", fontSize: "0.65rem", cursor: "pointer",
          }}
        >Change</button>
      </div>

      {/* Two-pane browse area */}
      <div style={{ display: "flex", gap: 10, flex: 1, minHeight: 0 }}>

        {/* Left: community / domain tree */}
        <div style={{
          width: 200, flexShrink: 0, border: `1px solid ${T.border}`,
          borderRadius: 8, overflowY: "auto", background: T.bgSection,
        }}>
          <div style={{ padding: "6px 10px 4px", ...sectionLabelSt }}>Communities</div>
          {communities.length === 0 && (
            <div style={{ padding: "12px 10px", fontSize: "0.76rem", color: T.textMuted }}>
              No communities loaded
            </div>
          )}
          {communities.map(comm => {
            const open = expanded.has(comm.id);
            const doms = domains[comm.id] ?? [];
            return (
              <div key={comm.id}>
                <div
                  onClick={() => toggleCommunity(comm.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "7px 10px",
                    cursor: "pointer", background: T.bgCard, borderBottom: `1px solid ${T.border}`,
                    userSelect: "none",
                  }}
                >
                  <span style={{
                    fontSize: "0.6rem", color: open ? T.brand : T.textMuted,
                    transform: open ? "rotate(90deg)" : "none",
                    display: "inline-block", transition: "transform 0.15s",
                  }}>▶</span>
                  <span>🏛</span>
                  <span style={{ fontSize: "0.78rem", fontWeight: 600, color: T.textPrimary, flex: 1 }}>
                    {comm.name}
                  </span>
                  {domLoading[comm.id] && (
                    <span style={{ fontSize: "0.65rem", color: T.textMuted }}>…</span>
                  )}
                </div>
                {open && doms.map((dom: any, i: number) => (
                  <div
                    key={dom.id}
                    onClick={() => handleDomainSelect(dom)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "6px 10px 6px 26px", cursor: "pointer",
                      borderBottom: `1px solid ${T.border}`,
                      background: selDomain?.id === dom.id ? T.brandLight : i % 2 === 0 ? T.bg : T.bgCard,
                      borderLeft: selDomain?.id === dom.id ? `3px solid ${T.brand}` : "3px solid transparent",
                    }}
                  >
                    <span>📁</span>
                    <span style={{
                      fontSize: "0.74rem", color: selDomain?.id === dom.id ? T.brand : T.textPrimary,
                      flex: 1, fontWeight: selDomain?.id === dom.id ? 600 : 400,
                    }}>{dom.name}</span>
                    {dom.type && (
                      <span style={{
                        fontSize: "0.55rem", color: T.textMuted, background: T.bg,
                        border: `1px solid ${T.border}`, borderRadius: 3, padding: "1px 4px",
                      }}>{dom.type}</span>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Right: term list */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
          {!selDomain ? (
            <div style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
              border: `1px solid ${T.border}`, borderRadius: 8, background: T.bgSection,
            }}>
              <div style={{ textAlign: "center", color: T.textMuted, fontSize: "0.82rem" }}>
                Select a domain to browse its business terms
              </div>
            </div>
          ) : (
            <>
              {/* Domain header + search */}
              <div style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                background: T.brandLight, border: `1px solid ${T.brandMid}`, borderRadius: 6,
              }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: T.brand }}>
                  📁 {selDomain.name}
                </span>
                <button
                  onClick={() => { setSelDomain(null); setBtList([]); }}
                  style={{
                    marginLeft: "auto", background: "none", border: `1px solid ${T.brandMid}`,
                    color: T.brand, borderRadius: 4, padding: "2px 8px", fontSize: "0.65rem", cursor: "pointer",
                  }}
                >Change</button>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  style={{ ...inputSt, flex: 1 }}
                  placeholder="🔍 Search terms…"
                  value={btSearch}
                  onChange={e => handleSearch(e.target.value)}
                />
                <span style={{ fontSize: "0.72rem", color: T.textMuted, whiteSpace: "nowrap" }}>
                  {btList.length}/{btTotal}
                </span>
                <button
                  onClick={() => setSelected(new Set(btList.map(t => t.id)))}
                  style={{
                    fontSize: "0.68rem", padding: "3px 8px", borderRadius: 4, cursor: "pointer",
                    background: T.bgCard, border: `1px solid ${T.border}`, color: T.textMuted,
                  }}
                >All</button>
                <button
                  onClick={() => setSelected(new Set())}
                  style={{
                    fontSize: "0.68rem", padding: "3px 8px", borderRadius: 4, cursor: "pointer",
                    background: T.bgCard, border: `1px solid ${T.border}`, color: T.textMuted,
                  }}
                >None</button>
                <span style={{ fontSize: "0.72rem", color: T.textMuted, whiteSpace: "nowrap" }}>
                  {selected.size} sel
                </span>
              </div>

              {btLoading && btList.length === 0 && (
                <div style={{ textAlign: "center", padding: 14, color: T.textMuted, fontSize: "0.82rem" }}>
                  Loading terms…
                </div>
              )}

              <TermTable
                terms={btList.map(t => ({ id: t.id, term: t.name || t.term, definition: t.definition, domain: t.domain }))}
                selected={selected}
                onToggle={toggleSel}
                selectable
                maxH={240}
              />

              {/* Footer */}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {btOffset < btTotal && (
                  <button
                    onClick={() => selDomain && fetchBTs(selDomain.id, btOffset, btSearch, false)}
                    disabled={btLoading}
                    style={{
                      padding: "4px 12px", borderRadius: 5, cursor: "pointer",
                      background: T.bgCard, border: `1px solid ${T.border}`,
                      color: T.textMuted, fontSize: "0.72rem", opacity: btLoading ? 0.7 : 1,
                    }}
                  >{btLoading ? "Loading…" : `Load more (${btTotal - btOffset})`}</button>
                )}
                <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                  {savedMsg && (
                    <span style={{ fontSize: "0.72rem", fontWeight: 700, color: T.green }}>{savedMsg}</span>
                  )}
                  {!savedMsg && (
                    <button
                      onClick={handleSave}
                      disabled={selected.size === 0 || saving}
                      style={{
                        padding: "6px 18px", borderRadius: 6,
                        cursor: selected.size > 0 ? "pointer" : "default",
                        background: selected.size > 0 ? T.brand : T.border,
                        border: `1px solid ${selected.size > 0 ? T.brand : T.border}`,
                        color: selected.size > 0 ? "#fff" : T.textMuted,
                        fontSize: "0.76rem", fontWeight: 700, opacity: saving ? 0.7 : 1,
                      }}
                    >{saving ? "Saving…" : `Save ${selected.size > 0 ? `${selected.size} terms` : ""}`}</button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ALATION IMPORT PANEL ─────────────────────────────────────────────────────

function AlationImportPanel({
  appNm, entityId, onTermsImported,
}: {
  appNm: string;
  entityId: string;
  onTermsImported: (terms: ImportedTerm[]) => void;
}) {
  const [creds,     setCreds]     = useState<CatalogCreds>({ host: "", auth_type: "token" });
  const [testState, setTestState] = useState<"idle" | "testing" | "ok" | "error">("idle");
  const [testMsg,   setTestMsg]   = useState("");
  const [btList,    setBtList]    = useState<any[]>([]);
  const [btTotal,   setBtTotal]   = useState(0);
  const [btOffset,  setBtOffset]  = useState(0);
  const [btSearch,  setBtSearch]  = useState("");
  const [btLoading, setBtLoading] = useState(false);
  const [selected,  setSelected]  = useState<Set<string>>(new Set());
  const [saving,    setSaving]    = useState(false);
  const [savedMsg,  setSavedMsg]  = useState("");
  const BT_LIMIT = 50;

  const handleTest = async () => {
    setTestState("testing");
    const r = await postCatalogAction({
      action: "TEST_CONNECTION", target: "alation",
      entity_id: entityId, app_nm: appNm, creds,
    });
    if (r.success) { setTestState("ok"); fetchBTs(0, "", true); }
    else { setTestState("error"); setTestMsg(r.msg ?? "Connection failed"); }
  };

  const fetchBTs = async (offset: number, search: string, replace: boolean) => {
    setBtLoading(true);
    const r = await postCatalogAction({
      action: "FETCH_BUSINESS_TERMS", target: "alation",
      entity_id: entityId, app_nm: appNm, creds,
      offset, limit: BT_LIMIT, search,
    });
    setBtLoading(false);
    if (r.success) {
      setBtTotal(r.data.total ?? 0);
      setBtList(replace ? r.data.terms : (p: any[]) => [...p, ...r.data.terms]);
      setBtOffset((replace ? 0 : btOffset) + r.data.terms.length);
    }
  };

  const handleSearch = (s: string) => {
    setBtSearch(s); setBtList([]); setBtOffset(0); fetchBTs(0, s, true);
  };

  const handleSave = async () => {
    const toSave = btList.filter(t => selected.has(t.id));
    if (!toSave.length) return;
    setSaving(true);
    await apiPost(`/adriva_api/dg/glossary/${appNm}/upsert`, {
      terms: toSave.map(t => ({
        term: t.name || t.term, description: t.definition || t.description,
        domain: t.domain, source: "alation", alation_id: t.id, status: "draft",
      })),
    });
    setSaving(false);
    const imported: ImportedTerm[] = toSave.map(t => ({
      id: t.id, term: t.name || t.term, definition: t.definition || t.description,
      domain: t.domain, source: "alation" as const, source_id: t.id,
    }));
    setSavedMsg(`✓ ${imported.length} terms saved`);
    onTermsImported(imported);
  };

  const toggleSel = (id: string) =>
    setSelected(p => { const s = new Set(p); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // ── Connection form ──────────────────────────────────────────────────────────
  if (testState !== "ok") return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 10,
      padding: "14px 16px", background: T.bgCard,
      border: `1px solid ${T.border}`, borderRadius: 8,
    }}>
      <div>
        <label style={labelSt}>Alation Host URL</label>
        <input
          style={inputSt}
          value={creds.host}
          onChange={e => setCreds({ ...creds, host: e.target.value })}
          placeholder="https://yourorg.alationcloud.com"
        />
      </div>
      {/* Auth type toggle */}
      <div style={{ display: "flex", gap: 6, marginBottom: 2 }}>
        {(["token", "basic"] as const).map(at => (
          <button
            key={at}
            onClick={() => setCreds({ ...creds, auth_type: at })}
            style={{
              padding: "4px 14px", borderRadius: 5, cursor: "pointer",
              background: creds.auth_type === at ? T.brandLight : "transparent",
              border: `1px solid ${creds.auth_type === at ? T.brandMid : T.border}`,
              color: creds.auth_type === at ? T.brand : T.textMuted,
              fontSize: "0.72rem", fontWeight: creds.auth_type === at ? 700 : 400,
            }}
          >
            {at === "token" ? "API Token" : "Username / Password"}
          </button>
        ))}
      </div>
      {creds.auth_type === "token" ? (
        <div>
          <label style={labelSt}>API Token</label>
          <input style={inputSt} type="password" value={creds.api_token ?? ""}
            onChange={e => setCreds({ ...creds, api_token: e.target.value })} />
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={labelSt}>Username</label>
            <input style={inputSt} value={creds.username ?? ""} autoComplete="off"
              onChange={e => setCreds({ ...creds, username: e.target.value })} />
          </div>
          <div>
            <label style={labelSt}>Password</label>
            <input style={inputSt} type="password" value={creds.password ?? ""} autoComplete="off"
              onChange={e => setCreds({ ...creds, password: e.target.value })} />
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          onClick={handleTest}
          disabled={testState === "testing"}
          style={{
            padding: "6px 18px", borderRadius: 6, cursor: "pointer",
            background: T.brand, border: `1px solid ${T.brand}`,
            color: "#fff", fontSize: "0.78rem", fontWeight: 700,
            opacity: testState === "testing" ? 0.7 : 1,
          }}
        >{testState === "testing" ? "Connecting…" : "Connect"}</button>
        {testState === "error" && (
          <span style={{ fontSize: "0.72rem", color: T.red }}>{testMsg}</span>
        )}
      </div>
    </div>
  );

  // ── Connected: flat term list (no community tree for Alation) ────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 0 }}>

      {/* Connected bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "7px 12px",
        background: T.greenBg, border: `1px solid ${T.greenBorder}`, borderRadius: 6,
      }}>
        <span style={{ fontSize: "0.78rem", fontWeight: 700, color: T.green }}>
          ✓ Connected · {creds.host}
        </span>
        <button
          onClick={() => { setTestState("idle"); setBtList([]); }}
          style={{
            marginLeft: "auto", background: "none", border: `1px solid ${T.greenBorder}`,
            color: T.green, borderRadius: 4, padding: "2px 8px", fontSize: "0.65rem", cursor: "pointer",
          }}
        >Change</button>
      </div>

      {/* Search + load */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          style={{ ...inputSt, flex: 1 }}
          placeholder="🔍 Search business terms…"
          value={btSearch}
          onChange={e => handleSearch(e.target.value)}
        />
        {btList.length === 0 && !btLoading && (
          <button
            onClick={() => fetchBTs(0, "", true)}
            style={{
              padding: "6px 14px", borderRadius: 6, cursor: "pointer",
              background: T.brandLight, border: `1px solid ${T.brandMid}`,
              color: T.brand, fontSize: "0.76rem", fontWeight: 700,
            }}
          >Load Terms</button>
        )}
      </div>

      {btLoading && btList.length === 0 && (
        <div style={{ textAlign: "center", padding: 16, color: T.textMuted, fontSize: "0.82rem" }}>
          Loading terms…
        </div>
      )}

      {btList.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: "0.72rem", color: T.textMuted }}>
              {btList.length}/{btTotal} · {selected.size} sel
            </span>
            <button
              onClick={() => setSelected(new Set(btList.map(t => t.id)))}
              style={{
                fontSize: "0.68rem", padding: "3px 8px", borderRadius: 4, cursor: "pointer",
                background: T.bgCard, border: `1px solid ${T.border}`, color: T.textMuted,
              }}
            >All</button>
            <button
              onClick={() => setSelected(new Set())}
              style={{
                fontSize: "0.68rem", padding: "3px 8px", borderRadius: 4, cursor: "pointer",
                background: T.bgCard, border: `1px solid ${T.border}`, color: T.textMuted,
              }}
            >None</button>
          </div>

          <TermTable
            terms={btList.map(t => ({ id: t.id, term: t.name || t.term, definition: t.definition, domain: t.domain }))}
            selected={selected}
            onToggle={toggleSel}
            selectable
            maxH={240}
          />

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {btOffset < btTotal && (
              <button
                onClick={() => fetchBTs(btOffset, btSearch, false)}
                disabled={btLoading}
                style={{
                  padding: "4px 12px", borderRadius: 5, cursor: "pointer",
                  background: T.bgCard, border: `1px solid ${T.border}`,
                  color: T.textMuted, fontSize: "0.72rem", opacity: btLoading ? 0.7 : 1,
                }}
              >{btLoading ? "Loading…" : `Load more (${btTotal - btOffset})`}</button>
            )}
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
              {savedMsg && (
                <span style={{ fontSize: "0.72rem", fontWeight: 700, color: T.green }}>{savedMsg}</span>
              )}
              {!savedMsg && (
                <button
                  onClick={handleSave}
                  disabled={selected.size === 0 || saving}
                  style={{
                    padding: "6px 18px", borderRadius: 6,
                    cursor: selected.size > 0 ? "pointer" : "default",
                    background: selected.size > 0 ? T.brand : T.border,
                    border: `1px solid ${selected.size > 0 ? T.brand : T.border}`,
                    color: selected.size > 0 ? "#fff" : T.textMuted,
                    fontSize: "0.76rem", fontWeight: 700, opacity: saving ? 0.7 : 1,
                  }}
                >{saving ? "Saving…" : `Save ${selected.size > 0 ? `${selected.size} terms` : ""}`}</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── MAIN MODAL ───────────────────────────────────────────────────────────────

export default function BusinessTermConfigModal({
  show, onHide, appNm, entity, entityId, config, onChange, onSave,
}: BusinessTermConfigModalProps) {
  const [mode,           setMode]           = useState<BTMode>(config.mode ?? "generate");
  const [importedTerms,  setImportedTerms]  = useState<ImportedTerm[]>(config.terms ?? []);
  const [importSource,   setImportSource]   = useState<"excel" | "collibra" | "alation">("excel");
  const [glossaryCount,  setGlossaryCount]  = useState(config.glossary_count ?? config.glossary_term_count ?? 0);
  const [glossaryPreview,setGlossaryPreview]= useState<GlossaryPreviewTerm[]>([]);
  const [countLoading,   setCountLoading]   = useState(false);

  const isImportMode = mode === "excel" || mode === "collibra" || mode === "alation";
  const activeLeft   = isImportMode ? "import" : mode;

  const loadGlossaryCount = useCallback(async () => {
    if (!appNm || !show) return;
    setCountLoading(true);
    const params = new URLSearchParams({ entity: entity || "" });
    const r = await apiGet(`/adriva_api/dg/glossary/${appNm}/count?${params}`);
    if (r.success) { setGlossaryCount(r.count); setGlossaryPreview(r.preview ?? []); }
    setCountLoading(false);
  }, [appNm, entity, show]);

  useEffect(() => {
    if (show) {
      setMode(config.mode ?? "generate");
      setImportedTerms(config.terms ?? []);
      loadGlossaryCount();
    }
  }, [show]);

  const hasGlossary  = glossaryCount > 0;
  const hasImported  = importedTerms.length > 0;

  const handleTermsImported = (terms: ImportedTerm[]) => {
    setImportedTerms(terms);
    setMode(importSource);
    loadGlossaryCount();
    onChange({ mode: importSource, terms, glossary_count: glossaryCount, entity_filter: entity });
  };

  const handleSave = () => {
    const final: BusinessTermRunConfig = {
      mode, terms: importedTerms, glossary_count: glossaryCount, entity_filter: entity,
    };
    onSave(final);
    onHide();
  };

  const runDesc: Record<string, React.ReactNode> = {
    generate:    "LLM creates business terms from scratch — ideal for first-time source onboarding.",
    map_glossary: `LLM maps each field to your ${glossaryCount} glossary terms (match ≥ 0.60 = mapped, else new term generated).`,
    excel:       `LLM maps each field to your ${importedTerms.length} imported Excel terms.`,
    collibra:    `LLM maps each field to your ${importedTerms.length} Collibra terms.`,
    alation:     `LLM maps each field to your ${importedTerms.length} Alation terms.`,
  };

  const footerSummary: Record<string, string> = {
    generate:    "Mode: Generate New Terms",
    map_glossary:`Mode: Map to Glossary (${glossaryCount} terms)`,
    excel:       `Mode: Import & Map — ${importedTerms.length} Excel terms`,
    collibra:    `Mode: Import & Map — ${importedTerms.length} Collibra terms`,
    alation:     `Mode: Import & Map — ${importedTerms.length} Alation terms`,
  };

  if (!show) return null;

  // ── Left nav item ────────────────────────────────────────────────────────────
  const NavItem = ({
    id, icon, title, subtitle, badge,
  }: {
    id: string; icon: string; title: string; subtitle: string; badge?: React.ReactNode;
  }) => {
    const active = activeLeft === id;
    return (
      <div
        onClick={() => {
          if (id === "import") { setMode(importSource); }
          else if (id === "generate") { setMode("generate"); setImportedTerms([]); }
          else if (id === "map_glossary" && hasGlossary) { setMode("map_glossary"); }
        }}
        style={{
          padding: "12px 16px", cursor: id === "map_glossary" && !hasGlossary ? "not-allowed" : "pointer",
          borderBottom: `1px solid ${T.border}`,
          borderLeft: `3px solid ${active ? T.brand : "transparent"}`,
          background: active ? T.bgCard : "transparent",
          opacity: id === "map_glossary" && !hasGlossary ? 0.5 : 1,
          transition: "background 0.1s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
          <RadioDot active={active} />
          <span style={{ fontSize: "0.82rem", fontWeight: 700, color: active ? T.brand : T.textPrimary }}>
            {icon} {title}
          </span>
          {badge}
        </div>
        <div style={{ fontSize: "0.7rem", color: T.textMuted, paddingLeft: 24, lineHeight: 1.4 }}>
          {subtitle}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(15,23,42,0.5)", backdropFilter: "blur(2px)",
      zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
    }}>
      <style>{`
        *{box-sizing:border-box;}
        button:hover:not(:disabled){opacity:0.88;}
        input:focus{outline:none!important;border-color:${T.borderFocus}!important;box-shadow:0 0 0 3px rgba(99,102,241,0.1)!important;}
        ::-webkit-scrollbar{width:4px;}
        ::-webkit-scrollbar-thumb{background:${T.border};border-radius:99px;}
      `}</style>

      <div style={{
        background: T.bgCard, borderRadius: 14,
        width: "min(1260px, 96vw)", height: "min(680px, 92vh)",
        display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 24px 64px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)",
        borderTop: `3px solid ${T.brand}`,
      }}>

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div style={{
          padding: "16px 22px 14px", borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          gap: 12,
        }}>
          <div>
            <div style={{
              fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.1em",
              textTransform: "uppercase", color: T.brand, marginBottom: 4,
            }}>📚 Business Term Configuration</div>
            <div style={{ fontSize: "0.98rem", fontWeight: 700, color: T.textPrimary }}>
              {entity ? `Entity: ${entity}` : "All Entities"}
            </div>
            <div style={{ fontSize: "0.74rem", color: T.textMuted, marginTop: 2 }}>
              Controls how the LLM handles business terms for this discovery run
            </div>
          </div>

          {/* Glossary status badge */}
          <div style={{
            padding: "7px 12px", borderRadius: 8, flexShrink: 0,
            background: hasGlossary ? T.greenBg : T.amberBg,
            border: `1px solid ${hasGlossary ? T.greenBorder : T.amberBorder}`,
          }}>
            {countLoading ? (
              <span style={{ fontSize: "0.74rem", color: T.textMuted }}>Checking…</span>
            ) : hasGlossary ? (
              <span style={{ fontSize: "0.74rem", fontWeight: 700, color: T.green }}>
                ✓ {glossaryCount} terms in glossary
              </span>
            ) : (
              <span style={{ fontSize: "0.74rem", color: T.amber }}>⚠ No glossary terms yet</span>
            )}
          </div>

          <button
            onClick={onHide}
            style={{
              background: "none", border: "none", color: T.textMuted,
              fontSize: 18, cursor: "pointer", padding: "2px 6px", flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* ── Body: left nav + right content ─────────────────────────────────── */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

          {/* Left sidebar */}
          <div style={{
            width: 230, flexShrink: 0, borderRight: `1px solid ${T.border}`,
            display: "flex", flexDirection: "column", background: T.bgSection,
          }}>
            <div style={{ padding: "10px 16px 6px", ...sectionLabelSt }}>Mode</div>

            <NavItem
              id="generate"
              icon="🤖"
              title="Generate New"
              subtitle="LLM creates terms from scratch"
            />

            <NavItem
              id="map_glossary"
              icon="🗂"
              title="Map to Glossary"
              subtitle={hasGlossary ? `Match to ${glossaryCount} existing terms` : "No terms yet"}
              badge={hasGlossary
                ? <span style={{
                    fontSize: "0.62rem", fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                    background: T.greenBg, color: T.green, border: `1px solid ${T.greenBorder}`,
                  }}>{glossaryCount}</span>
                : null}
            />

            <NavItem
              id="import"
              icon="📥"
              title="Import & Map"
              subtitle="From Excel, Collibra, or Alation"
              badge={hasImported
                ? <span style={{
                    fontSize: "0.62rem", fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                    background: T.greenBg, color: T.green, border: `1px solid ${T.greenBorder}`,
                  }}>✓ {importedTerms.length}</span>
                : null}
            />

            {/* Run behaviour summary */}
            <div style={{
              marginTop: "auto", padding: "12px 14px",
              borderTop: `1px solid ${T.border}`,
            }}>
              <div style={{
                padding: "9px 12px", borderRadius: 7,
                background: T.brandLight, border: `1px solid ${T.brandMid}`,
                fontSize: "0.7rem", color: T.brand, lineHeight: 1.55,
              }}>
                <strong>Run behaviour:</strong><br />
                {runDesc[mode] ?? runDesc["generate"]}
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            overflow: "hidden", background: T.bg,
          }}>

            {/* ── GENERATE panel ──────────────────────────────────────────── */}
            {activeLeft === "generate" && (
              <div style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                padding: "30px 40px",
              }}>
                <div style={{
                  textAlign: "center", maxWidth: 360,
                  display: "flex", flexDirection: "column", gap: 14, alignItems: "center",
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: T.brandLight, display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: "1.6rem",
                  }}>🤖</div>
                  <div>
                    <div style={{ fontSize: "0.96rem", fontWeight: 700, color: T.textPrimary, marginBottom: 8 }}>
                      Generate New Terms
                    </div>
                    <div style={{ fontSize: "0.78rem", color: T.textMuted, lineHeight: 1.65 }}>
                      The LLM will create human-friendly business term names and definitions for each
                      field — ideal when onboarding a data source for the first time.
                    </div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                    <TagPill label="Auto-naming" />
                    <TagPill label="Definitions generated" />
                    <TagPill label="No setup needed" />
                  </div>
                </div>
              </div>
            )}

            {/* ── MAP GLOSSARY panel ──────────────────────────────────────── */}
            {activeLeft === "map_glossary" && (
              <div style={{ flex: 1, overflowY: "auto", padding: "20px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <div style={{ fontSize: "0.88rem", fontWeight: 700, color: T.textPrimary, marginBottom: 4 }}>
                    Map to ADRIVA Glossary
                  </div>
                  <div style={{ fontSize: "0.76rem", color: T.textMuted, lineHeight: 1.6 }}>
                    The LLM will match each field to your {glossaryCount} existing glossary terms.
                    Fields with confidence below 60% will get new terms generated automatically.
                  </div>
                </div>

                {/* Glossary preview pills */}
                {glossaryPreview.length > 0 && (
                  <div>
                    <div style={sectionLabelSt}>Terms in Glossary</div>
                    <div style={{
                      padding: "12px 14px", borderRadius: 8,
                      background: T.bgCard, border: `1px solid ${T.border}`,
                      display: "flex", flexWrap: "wrap", gap: 6,
                    }}>
                      {glossaryPreview.map(t => (
                        <TagPill key={t.id} label={t.term} />
                      ))}
                      {glossaryCount > glossaryPreview.length && (
                        <span style={{ fontSize: "0.68rem", color: T.textMuted, padding: "2px 4px" }}>
                          +{glossaryCount - glossaryPreview.length} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div style={{
                  padding: "10px 14px", borderRadius: 8,
                  background: T.greenBg, border: `1px solid ${T.greenBorder}`,
                }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 700, color: T.green, marginBottom: 3 }}>
                    ✓ {glossaryCount} glossary terms ready
                  </div>
                  <div style={{ fontSize: "0.72rem", color: T.green }}>
                    Match threshold: 60% confidence
                  </div>
                </div>
              </div>
            )}

            {/* ── IMPORT panel ────────────────────────────────────────────── */}
            {activeLeft === "import" && (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

                {/* Source tabs */}
                <div style={{
                  padding: "12px 20px 0",
                  borderBottom: `1px solid ${T.border}`,
                  background: T.bgCard,
                }}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 0 }}>
                    {(["excel", "collibra", "alation"] as const).map(s => (
                      <button
                        key={s}
                        onClick={() => { setImportSource(s); setMode(s); }}
                        style={{
                          padding: "7px 14px", borderRadius: "6px 6px 0 0", cursor: "pointer",
                          background: importSource === s ? T.bg : "transparent",
                          border: `1px solid ${importSource === s ? T.border : "transparent"}`,
                          borderBottom: importSource === s ? `1px solid ${T.bg}` : "none",
                          color: importSource === s ? T.brand : T.textMuted,
                          fontSize: "0.76rem", fontWeight: importSource === s ? 700 : 500,
                          marginBottom: importSource === s ? -1 : 0,
                          position: "relative",
                          zIndex: importSource === s ? 1 : 0,
                        }}
                      >
                        {s === "excel" ? "📄 Excel / CSV" : s === "collibra" ? "⬡ Collibra" : "◈ Alation"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Panel content */}
                <div style={{ flex: 1, padding: "16px 20px", overflow: "hidden", display: "flex", flexDirection: "column", gap: 12 }}>
                  {importSource === "excel" && (
                    <ExcelImportPanel appNm={appNm} onTermsImported={handleTermsImported} />
                  )}
                  {importSource === "collibra" && (
                    <CollibraImportPanel appNm={appNm} entityId={entityId} onTermsImported={handleTermsImported} />
                  )}
                  {importSource === "alation" && (
                    <AlationImportPanel appNm={appNm} entityId={entityId} onTermsImported={handleTermsImported} />
                  )}

                  {/* Imported terms summary */}
                  {hasImported && (
                    <div style={{
                      padding: "9px 12px", borderRadius: 7,
                      background: T.greenBg, border: `1px solid ${T.greenBorder}`,
                      flexShrink: 0,
                    }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 700, color: T.green, marginBottom: 5 }}>
                        ✓ {importedTerms.length} terms imported from {mode} — agent will map fields to these
                      </div>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {importedTerms.slice(0, 6).map(t => (
                          <TagPill key={t.id} label={t.term} />
                        ))}
                        {importedTerms.length > 6 && (
                          <span style={{ fontSize: "0.65rem", color: T.textMuted }}>
                            +{importedTerms.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────────── */}
        <div style={{
          padding: "12px 22px", borderTop: `1px solid ${T.border}`,
          background: T.bgCard, display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={onHide}
            style={{
              padding: "7px 16px", borderRadius: 7, cursor: "pointer",
              background: T.bgCard, border: `1px solid ${T.border}`,
              color: T.textMuted, fontSize: "0.78rem", fontWeight: 500,
            }}
          >Cancel</button>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: "0.72rem", color: T.textMuted }}>
              {footerSummary[mode] ?? footerSummary["generate"]}
            </span>
            <button
              onClick={handleSave}
              style={{
                padding: "7px 22px", borderRadius: 7, cursor: "pointer",
                background: T.brand, border: `1px solid ${T.brand}`,
                color: "#fff", fontSize: "0.78rem", fontWeight: 700,
                boxShadow: "0 2px 8px rgba(79,70,229,0.2)",
              }}
            >✓ Save Configuration</button>
          </div>
        </div>
      </div>
    </div>
  );
}